import React, { useMemo } from 'react'

import { AddressDisplay, type CLSize } from '~/components/component-library'
import { Input } from '~/components/inputs/Input'
import { SupportedChainId } from '~/constants/chains'
import { useUpdateAddressSearchHistory } from '~/hooks/useAddressSearchHistory'
import { IAddress } from '~/types'
import { cn } from '~/utils/tailwind-utils'

type ValidAddressDisplayProps = {
  address: IAddress
  chainId?: SupportedChainId
  onClearInput: () => void
  disabled?: boolean
  disabledReason?: string
  isLoading?: boolean
  size?: CLSize
}

const UnmemoizedValidAddressDisplay = ({
  address,
  chainId,
  onClearInput,
  disabled = false,
  disabledReason,
  isLoading,
  size = 'base',
}: ValidAddressDisplayProps) => {
  // Stabilize asComponent so Input doesn't unmount/remount the inner
  // AddressDisplay (and its AddressPopover) on every parent render —
  // a fresh inline function gives `<Component>` a new identity each
  // render, which resets popover hover state mid-interaction.
  const asComponent = useMemo(() => {
    const ValidAddressInner = () => (
      <div
        // Reserve room for the absolutely-positioned ClearButton so the
        // shortened address suffix doesn't slide under the X.
        className={cn('flex w-full items-center', {
          'py-1.5 pr-6': size === 'small',
          'py-2.5 pr-8': size === 'base',
        })}
      >
        <AddressDisplay
          address={address}
          chainId={chainId}
          layout="standard"
          size={size}
          clickable={false}
        />
      </div>
    )
    ValidAddressInner.displayName = 'ValidAddressInner'
    return ValidAddressInner
  }, [address, chainId, size])

  return (
    <>
      <AddressSearchUpdater address={address} chainId={chainId} />
      <Input
        name="valid-address-display"
        onClearInput={disabled ? undefined : onClearInput}
        clearButtonClassName="hover:bg-transparent dark:hover:bg-transparent -right-3 absolute outline-none focus-visible:outline-none"
        disabled={disabled}
        disabledReason={disabledReason}
        isLoading={isLoading}
        asComponent={asComponent}
        backgroundShadow={false}
        focusRing={false}
        messagePositionClassName={'left-0'}
        messageClassName="text-sm"
        autoComplete="off"
        inputSize={size}
      />
    </>
  )
}

// NOTE: This component is only used to update the address search history without triggering re-renders
// Search results do not fetch all the data (farcaster/ens/contacts) that a valid address does
// So once the user selects a search result, we need to update the search history with the full address data
const AddressSearchUpdater = ({
  address,
  chainId,
}: {
  address: IAddress
  chainId?: SupportedChainId
}) => {
  useUpdateAddressSearchHistory({ address, chainId })
  return null
}

export const ValidAddressDisplay = React.memo(UnmemoizedValidAddressDisplay)
